import Deprecated from '../core/Deprecated';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../core/AddButton';
import CancelButton from '../core/CancelButton';
import Errored from '../core/Errored';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import SearchSelect from '../core/SearchSelect';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { computeLabelFromType, getResourceLabel, makeCancelable } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import ProjectLanguageSelector from '../core/ProjectLanguageSelector';
import ComponentLinkList from './ontologyproject/ComponentLinkList';

class FindNamedResourceDialog extends React.Component {

    constructor(props) {
        super(props);
        const allowEmpty = true;
        this.state = {
            open: false,
            allowEmpty,
            lang: props.viewLang || props.defaultLang,
            text: ""
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    handleClickOpen = async () => this.setState({ open: true });

    loadData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph, true));
        return this.cancellable;
    }

    changeLanguage = (lang) => this.setState({ lang });

    computeSearch = (value, lang) => {
        const { searchClasses } = this.props;
        let graphList = [this.props.graph];
        let exludeList = [
            ...this.props.exclude
        ];
        if (this.props.source) exludeList.push(this.props.source);
        let search = [
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: "http://www.w3.org/2002/07/owl#NamedIndividual"
            },
            {
                predicate: "http://www.w3.org/2000/01/rdf-schema#label",
                ...(value !== "") && {
                    value: value,
                    isExactMatch: false
                }
            }
        ];
        let fullSearch =
        {
            search,
            graph: graphList,
            excludeSystemNamespaces: true,
            exclude: [...new Set(exludeList)],
            language: lang,
            ignoreNoLanguage: lang !== "und",
            fast: value === "",
            basicSort: value === "",
            pageSize: 20,
            predicates: [
                "http://www.w3.org/2000/01/rdf-schema#label",
                "http://www.w3.org/2002/07/owl#deprecated",
                "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                "https://grafsync.graphifi.com/schema/gs/visibility"
            ]
        };
        if (searchClasses.length > 0) {
            fullSearch.search.push({
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: this.props.searchClasses,
                ...(this.props.inferSubclasses) && { inferenceGraph: this.props.associatedProjectGraph },
            });
        }
        // For asymmetric props do not allow in results if prop relationship in other direction already
        if (this.props.searchTypes.includes("Asymmetric") && this.props.source && this.props.property) {
            fullSearch.search.push({
                predicate: this.props.property,
                value: this.props.source,
                isText: false,
                invert: true
            });
        }
        return fullSearch;
    };

    includeResource = (id) => {
        this.props.onIncludeResource(id);
        this.exitFindResource();
    };

    exitFindResource = () => this.setState({ open: false, text: "" });

    updateSearchText = (text) => this.setState({ text });

    render() {
        const { classes, exclude, displayType, viewLang, sourceComponent } = this.props;
        const { text, isErrored, lang, allowEmpty } = this.state;

        if (isErrored) return <Errored data={isErrored} dataTest="fr-error" />;

        return (
            <ProjectConsumer>
                {({ id: projectIRI }) => (
                    <React.Fragment>
                        <AddButton dataTest="add-relationship" onClick={() => this.handleClickOpen()} title="Add a relationship" />
                        <Dialog
                            maxWidth="lg"
                            fullWidth
                            classes={{
                                paper: classes.paper,
                                paperWidthLg: classes.largeDialog
                            }}
                            className={classes.dialog}
                            open={this.state.open}
                            onClose={this.exitFindCResource}
                            aria-labelledby="find-resource-dialog-title">
                            <DialogTitle disableTypography className={classes.mainHeading} id="find-resource-dialog-title">Find Named Individual</DialogTitle>
                            <DialogContent style={{ height: 450 }}>
                                <div style={{ height: 350 }}>
                                    <TitledPanel dataTest="project-language" title="Language">
                                        <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={this.changeLanguage} />                                    </TitledPanel>
                                    <SearchSelect
                                        key={lang}
                                        dataTest="find-relationship"
                                        focus={true}
                                        exclude={exclude}
                                        sortByLength={true}
                                        updateSearchText={this.updateSearchText}
                                        allowEmpty={allowEmpty}
                                        maxLength={250}
                                        initialText={text}
                                        search={(value, lang) => this.computeSearch(value, lang, projectIRI)}
                                        listSecondIcon={(result) => {
                                            if (result.deprecated) return <Deprecated />;
                                            return null;
                                        }}
                                        listSelectionDisplay={(result, lang) => {
                                            const label = getResourceLabel(result, "label", lang);
                                            return label;
                                        }}
                                        listDisplay={(result, lang) => {
                                            const label = getResourceLabel(result, "label", lang);
                                            return <div>
                                                <Typography data-test="result-text" variant="body2">
                                                    <span style={{ wordBreak: 'break-word' }}>
                                                        <Tooltip
                                                            PopperProps={{
                                                                "data-test": "result-tip"
                                                            }}
                                                            title={result.id}>
                                                            <span data-test="result-text-words">{label}</span>
                                                        </Tooltip>
                                                    </span>
                                                </Typography>
                                                {displayType &&
                                                    <Typography data-test="caption" variant="caption">{computeLabelFromType(result.type)}</Typography>
                                                }
                                                <ComponentLinkList
                                                    data={result}
                                                    includeIsDefinedBy={true}
                                                    graph={result.inNamedGraph}
                                                    context={sourceComponent}
                                                    defaultLang={lang}
                                                />
                                            </div>;
                                        }}
                                        sortProp="label"
                                        isClickableResult={(result) => !result.invalid && !result.deprecated && !exclude.includes(result.id)}
                                        title="Find a named individual"
                                        lang={lang}
                                        chosen={this.includeResource} />
                                </div>
                            </DialogContent>
                            <DialogActions className={classes.dialogActions}>
                                <CancelButton onClick={this.exitFindResource} />
                            </DialogActions>
                        </Dialog>
                    </React.Fragment>
                )}
            </ProjectConsumer>
        );
    }
}

FindNamedResourceDialog.defaultProps = {
    exclude: [],
    searchClasses: [],
    inferSubclasses: false
};

FindNamedResourceDialog.propTypes = {
    searchClasses: PropTypes.array,
    graph: PropTypes.string.isRequired,
    property: PropTypes.string,
    source: PropTypes.string,
    sourceComponent: PropTypes.string,
    searchTypes: PropTypes.array,
    onIncludeResource: PropTypes.func.isRequired,
    viewLang: PropTypes.string,
    exclude: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(FindNamedResourceDialog);